import { useState } from "react";
import {
  usePaginatedQuery,
  type PaginatedDataHookResult,
  type PaginatedFilters,
  type UsePaginatedQueryOptions,
  type UsePaginatedQueryResult,
} from "@marekl/adminer/hooks";
import { formatPageRangeSummary } from "@marekl/adminer/components";
import { useT } from "@marekl/adminer/hooks";

const DEFAULT_LIMIT_OPTIONS = [10, 25, 50, 100];

export interface UsePagedListOptions extends UsePaginatedQueryOptions {
  /**
   * The noun in the GENITIVE for the footer summary, singular and plural:
   * `["kontaktu", "kontaktů"]` → "Zobrazeno 1–1 z 1 kontaktu", "… z 4
   * kontaktů". Czech takes the genitive after "z" for every count; the lib's
   * cs string fills `{noun}` from its nominative [one, few, many] forms
   * ("z 4 kontakty"), so this hands it genitive ones (ADMINER_PROPOSALS A15).
   */
  nounOf: readonly [one: string, many: string];
}

export interface UsePagedListResult<TItem> extends UsePaginatedQueryResult<TItem> {
  /**
   * "Showing X–Y of Z <noun>" against the backend's `total_estimate`.
   * Undefined while nothing is loaded or when the list is empty.
   */
  summary: string | undefined;
}

/**
 * `usePaginatedQuery` plus a page-range footer summary. The lib hook owns the
 * cursor + limit URL state; this wrapper additionally tracks the 1-based page
 * index (the cursor protocol doesn't carry one) so the footer can show
 * "Showing X–Y of Z". Like the lib's own back-stack, the index lives in
 * component state and is lost on hard refresh — acceptable.
 */
export function usePagedList<TFilters, TItem>(
  useDataHook: (filters: TFilters & PaginatedFilters) => PaginatedDataHookResult<TItem>,
  filters: TFilters,
  options: UsePagedListOptions,
): UsePagedListResult<TItem> {
  const { nounOf, limitOptions = DEFAULT_LIMIT_OPTIONS, ...rest } = options;
  const nounForms = [nounOf[0], nounOf[1], nounOf[1]] as const;
  const result = usePaginatedQuery(useDataHook, filters, { ...rest, limitOptions });
  const [page, setPage] = useState(1);
  // The Czech "Zobrazeno X–Y z Z …" comes from the dictionary.
  const t = useT();

  const { pagination } = result;
  const total = result.data?.total_estimate ?? 0;
  const summary = formatPageRangeSummary(
    {
      total,
      page,
      perPage: pagination.limit,
      totalPages: pagination.limit > 0 ? Math.ceil(total / pagination.limit) : 0,
    },
    { nounForms, t },
  );

  return {
    ...result,
    pagination: {
      ...pagination,
      onNext: () => {
        if (!pagination.hasNext) return;
        setPage((p) => p + 1);
        pagination.onNext();
      },
      onPrev: () => {
        setPage((p) => Math.max(1, p - 1));
        pagination.onPrev();
      },
      onLimitChange: (n) => {
        setPage(1);
        pagination.onLimitChange(n);
      },
    },
    resetPagination: () => {
      setPage(1);
      result.resetPagination();
    },
    filterReset: (updater) => {
      setPage(1);
      result.filterReset(updater);
    },
    summary,
  };
}
